import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';

import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import { reducer } from './reducer';
import type { SessionContextPluginState, SessionContextAction } from './types';

export const initialState: SessionContextPluginState = {
	adfResponse: undefined,
	status: 'idle',
	responseVersion: 0,
};

/**
 * This plugin will store the core business logic state for the AI experience and eventually
 * replace the xstate machine. The xstate machine is the source of truth for the AI experience
 * until all business logic is migrated to the plugin.
 */
export const createAISessionContextPlugin = (options: {
	dispatch: PMPluginFactoryParams['dispatch'];
}): SafePlugin => {
	const { dispatch } = options;
	return new SafePlugin<SessionContextPluginState>({
		key: aiSessionContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: SessionContextPluginState) => {
					const meta = tr.getMeta(aiSessionContextPluginKey);
					if (meta) {
						const newState = reducer({
							state: pluginState,
							action: meta as SessionContextAction,
						});
						if (newState !== pluginState) {
							dispatch(aiSessionContextPluginKey, newState);
						}
						return newState;
					}
				return pluginState;
			},
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): SessionContextPluginState | undefined => {
	return aiSessionContextPluginKey.getState(state);
};

export const createCommand = (action: SessionContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiSessionContextPluginKey, action);
	};
};
